/**
 * Model Router - Seleção dinâmica de modelo LLM baseado em complexidade
 * Versão 2.0: Usa modelos dinâmicos baseados nas API Keys do usuário
 */

import { UserModelsService, type UserModel } from './userModelsService'

export interface ComplexityAnalysis {
    score: number // 0-100
    category: 'simple' | 'medium' | 'complex'
    factors: {
        length: number
        keywords: string[]
        taskType: string
        expectedOutputSize: 'short' | 'medium' | 'long'
    }
}

export interface ModelConfig {
    provider: 'openai' | 'anthropic' | 'google'
    model: string
    maxTokens: number
    costPerInputToken: number
    costPerOutputToken: number
}

/**
 * Analisa a complexidade de uma mensagem do usuário
 */
export function analyzeComplexity(userMessage: string): ComplexityAnalysis {
    let score = 0
    const foundKeywords: string[] = []

    const keywords = {
        simple: ['o que é', 'defina', 'explique brevemente', 'resumo', 'qual é'],
        medium: ['como', 'por que', 'compare', 'liste', 'descreva'],
        complex: ['crie', 'desenvolva', 'analise profundamente', 'estudo completo', 'pesquisa', 'elabore']
    }

    // 1. Tamanho da mensagem
    const wordCount = userMessage.split(' ').length
    if (wordCount < 10) {
        score += 10
    } else if (wordCount < 30) {
        score += 30
    } else {
        score += 60
    }

    // 2. Palavras-chave de complexidade
    const lowerMessage = userMessage.toLowerCase()

    if (keywords.complex.some(kw => lowerMessage.includes(kw))) {
        score += 40
        foundKeywords.push(...keywords.complex.filter(kw => lowerMessage.includes(kw)))
    } else if (keywords.medium.some(kw => lowerMessage.includes(kw))) {
        score += 20
        foundKeywords.push(...keywords.medium.filter(kw => lowerMessage.includes(kw)))
    } else if (keywords.simple.some(kw => lowerMessage.includes(kw))) {
        score += 5
        foundKeywords.push(...keywords.simple.filter(kw => lowerMessage.includes(kw)))
    }

    // 3. Detectar tipo de tarefa
    let taskType = 'question'
    if (lowerMessage.includes('crie') || lowerMessage.includes('desenvolva')) {
        taskType = 'creation'
        score += 30
    } else if (lowerMessage.includes('analise') || lowerMessage.includes('estudo')) {
        taskType = 'analysis'
        score += 25
    }

    // 4. Categorizar
    let category: 'simple' | 'medium' | 'complex'
    if (score < 30) {
        category = 'simple'
    } else if (score < 60) {
        category = 'medium'
    } else {
        category = 'complex'
    }

    return {
        score,
        category,
        factors: {
            length: wordCount,
            keywords: foundKeywords,
            taskType,
            expectedOutputSize: score < 30 ? 'short' : score < 60 ? 'medium' : 'long'
        }
    }
}

/**
 * Estima o número de tokens de uma mensagem
 */
export function estimateTokens(message: string): { input: number; output: number } {
    // Aproximação: 1 token ≈ 4 caracteres
    const inputTokens = Math.ceil(message.length / 4)

    // Estimar output baseado no input
    let outputTokens = inputTokens * 3 // Default: 3x o input

    if (message.includes('crie') || message.includes('desenvolva')) {
        outputTokens = inputTokens * 10 // Tarefas de criação geram mais output
    }

    return { input: inputTokens, output: outputTokens }
}

/**
 * Calcula o custo estimado de uma requisição
 */
export function estimateCost(
    inputTokens: number,
    outputTokens: number,
    model: ModelConfig
): number {
    const inputCost = (inputTokens / 1000) * model.costPerInputToken * 1000
    const outputCost = (outputTokens / 1000) * model.costPerOutputToken * 1000
    return inputCost + outputCost
}

/**
 * Classe principal do Model Router (Versão Dinâmica)
 */
export class ModelRouter {
    private userModelsService = new UserModelsService()

    /**
     * Roteia uma mensagem para o modelo mais adequado
     * Usa apenas modelos ativos do usuário (baseado em API Keys)
     */
    async routeMessage(
        userId: string,
        message: string
    ): Promise<{
        model: ModelConfig
        estimatedCost: number
        reasoning: string
        analysis: ComplexityAnalysis
    }> {
        // 1. Buscar modelos ativos do usuário
        const activeModels = await this.userModelsService.getUserActiveModels(userId)

        if (activeModels.length === 0) {
            throw new Error(
                'Nenhum modelo disponível. Adicione uma API Key nas configurações (OpenAI, Anthropic ou Google).'
            )
        }

        // 2. Analisar complexidade
        const analysis = analyzeComplexity(message)

        // 3. Selecionar melhor modelo disponível
        const model = this.selectBestAvailableModel(activeModels, analysis.category)

        // 4. Estimar custo
        const tokens = estimateTokens(message)
        const cost = estimateCost(tokens.input, tokens.output, model)

        return {
            model,
            estimatedCost: cost,
            reasoning: this.generateReasoning(analysis, model),
            analysis
        }
    }

    /**
     * Seleciona o melhor modelo disponível baseado na categoria
     */
    private selectBestAvailableModel(
        activeModels: UserModel[],
        category: 'simple' | 'medium' | 'complex'
    ): ModelConfig {
        // Mapear categoria para tier
        const tierMap = {
            simple: 'economy',
            medium: 'balanced',
            complex: 'premium'
        }

        const desiredTier = tierMap[category]

        // Tentar encontrar modelo do tier desejado
        let selectedModel = activeModels.find(m => m.tier === desiredTier)

        // Se não encontrar, usar o mais barato disponível (fallback)
        if (!selectedModel) {
            selectedModel = activeModels[0] // Já ordenado por custo (ascending)
        }

        return {
            provider: selectedModel.provider as 'openai' | 'anthropic' | 'google',
            model: selectedModel.model_name,
            maxTokens: selectedModel.max_tokens,
            costPerInputToken: selectedModel.cost_per_input_token,
            costPerOutputToken: selectedModel.cost_per_output_token
        }
    }

    private generateReasoning(
        analysis: ComplexityAnalysis,
        model: ModelConfig
    ): string {
        return `Selecionado ${model.model} (${analysis.category}) - Score: ${analysis.score}, Tipo: ${analysis.factors.taskType}`
    }
}
